/**
 * PostMessage hook for the Page Editor ↔ Storefront customizer preview.
 *
 * Follows the proven usePostMessage pattern from mobile-design:
 * - Queues messages until PREVIEW_READY is received
 * - Flushes queue on ready
 * - Tracks target iframe windows via Set<Window>
 * - Validates origin using storefront origin pattern
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { isAllowedStorefrontOrigin } from '../mobile-design/preview-protocol'
import {
  MSG_PAGE_CONFIG_UPDATE,
  MSG_PREVIEW_TOKEN_REFRESH,
  MSG_PREVIEW_READY,
  MSG_SECTION_PROP_PATCH,
  MSG_APPEARANCE_RESPONSIVE_PATCH,
  MSG_UPDATE_BLOCK,
  MSG_HIGHLIGHT_BLOCK,
  type PageConfigUpdateMessage,
  type PageEditorMessage,
  type SectionPropPatchPayload,
  type AppearanceResponsivePatchPayload,
} from './page-editor-protocol'
import type { BlockContent } from '@zaviago/storefront-config'

export function usePageEditorPostMessage(storefrontUrl: string | null) {
  const [isReady, setIsReady] = useState(false)
  const targetWindowsRef = useRef<Set<Window>>(new Set())
  const targetOriginRef = useRef<string>('')
  const queueRef = useRef<PageEditorMessage[]>([])

  // Extract origin from storefront URL
  useEffect(() => {
    if (storefrontUrl) {
      try {
        targetOriginRef.current = new URL(storefrontUrl).origin
      } catch {
        // Invalid URL — leave origin empty
      }
    }
  }, [storefrontUrl])

  // Listen for PREVIEW_READY from iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!isAllowedStorefrontOrigin(event.origin, targetOriginRef.current)) return
      if (event.data?.type === MSG_PREVIEW_READY) {
        const source = event.source as Window
        targetWindowsRef.current.add(source)
        setIsReady(true)

        // Flush queued messages to the newly-ready window
        const origin = targetOriginRef.current || event.origin
        for (const msg of queueRef.current) {
          source.postMessage(msg, origin)
        }
        queueRef.current = []
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const sendMessage = useCallback((msg: PageEditorMessage) => {
    const origin = targetOriginRef.current
    if (targetWindowsRef.current.size > 0 && origin) {
      for (const win of targetWindowsRef.current) {
        try {
          win.postMessage(msg, origin)
        } catch {
          targetWindowsRef.current.delete(win)
        }
      }
    } else {
      queueRef.current.push(msg)
    }
  }, [])

  const sendConfigUpdate = useCallback(
    (config: PageConfigUpdateMessage['config']) => {
      sendMessage({ type: MSG_PAGE_CONFIG_UPDATE, config })
    },
    [sendMessage],
  )

  const sendTokenRefresh = useCallback(
    (token: string) => {
      sendMessage({ type: MSG_PREVIEW_TOKEN_REFRESH, token })
    },
    [sendMessage],
  )

  const sendCosmeticPatch = useCallback(
    (patch: SectionPropPatchPayload) => {
      sendMessage({ type: MSG_SECTION_PROP_PATCH, patch })
    },
    [sendMessage],
  )

  const sendAppearancePatch = useCallback(
    (patch: AppearanceResponsivePatchPayload) => {
      sendMessage({ type: MSG_APPEARANCE_RESPONSIVE_PATCH, patch })
    },
    [sendMessage],
  )

  const sendBlockUpdate = useCallback(
    (sectionPath: string, blockId: string, content: BlockContent) => {
      sendMessage({ type: MSG_UPDATE_BLOCK, sectionPath, blockId, content })
    },
    [sendMessage],
  )

  const sendHighlightBlock = useCallback(
    (sectionPath: string, blockId: string | null) => {
      sendMessage({ type: MSG_HIGHLIGHT_BLOCK, sectionPath, blockId })
    },
    [sendMessage],
  )

  return { isReady, sendConfigUpdate, sendTokenRefresh, sendCosmeticPatch, sendAppearancePatch, sendBlockUpdate, sendHighlightBlock }
}
